/**
 * Compact shareable state: board + turn + mode + difficulty (game not over).
 * Uses short query params to stay readable.
 */

/** @typedef {import('./gameLogic').Board} Board */

const CHAR = { empty: '-', X: 'x', O: 'o' };
const REV = { '-': null, x: 'X', o: 'O' };

/**
 * @param {Board} board
 */
export function boardToString(board) {
  return board
    .map((c) => {
      if (c === 'X') return CHAR.X;
      if (c === 'O') return CHAR.O;
      return CHAR.empty;
    })
    .join('');
}

/**
 * @param {string} s
 * @returns {Board | null}
 */
export function stringToBoard(s) {
  if (typeof s !== 'string' || s.length !== 9) return null;
  const b = [];
  for (let i = 0; i < 9; i += 1) {
    const ch = s[i];
    if (!(ch in REV)) return null;
    b.push(REV[ch]);
  }
  return /** @type {Board} */ (b);
}

/**
 * @param {object} state
 * @param {Board} state.board
 * @param {'X' | 'O'} state.currentPlayer
 * @param {'pvp' | 'ai'} state.mode
 * @param {'easy' | 'medium' | 'hard'} state.difficulty
 * @param {boolean} state.gameOver
 */
export function encodeGameParams({ board, currentPlayer, mode, difficulty, gameOver }) {
  if (gameOver) return {};
  const b = boardToString(board);
  if (b === '---------') return {};
  return {
    b,
    t: currentPlayer === 'X' ? 'x' : 'o',
    m: mode,
    d: difficulty,
  };
}

/**
 * @param {URLSearchParams} params
 * @returns {{
 *   board: Board | null,
 *   currentPlayer: 'X' | 'O' | null,
 *   mode: 'pvp' | 'ai' | null,
 *   difficulty: 'easy' | 'medium' | 'hard' | null,
 * } | null}
 */
export function decodeGameParams(params) {
  const bRaw = params.get('b');
  const tRaw = params.get('t');
  const mRaw = params.get('m');
  const dRaw = params.get('d');

  let board = null;
  if (bRaw) {
    board = stringToBoard(bRaw);
    if (!board) return null;
  }

  let currentPlayer = null;
  if (tRaw === 'x' || tRaw === 'X') currentPlayer = 'X';
  else if (tRaw === 'o' || tRaw === 'O') currentPlayer = 'O';

  let mode = null;
  if (mRaw === 'pvp' || mRaw === 'ai') mode = mRaw;

  let difficulty = null;
  if (dRaw === 'easy' || dRaw === 'medium' || dRaw === 'hard') difficulty = dRaw;

  return { board, currentPlayer, mode, difficulty };
}
